/**
 * Smart battery system — tab / thumbnail synced hero + mobile copy (Dawn-style custom element).
 */
class SmartBatterySystem extends HTMLElement {
  constructor() {
    super();
    this._onResize = this._onResize.bind(this);
    this._onTabRowScroll = this._onTabRowScroll.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    /** @type {Element[]} */
    this.panels = [];
    /** @type {Element[]} */
    this.moCopies = [];
    /** @type {Element[]} */
    this.triggers = [];
    this.switchTimer = null;
    this.currentIndex = 0;
    this.autoEnabled = false;
    this.autoInterval = 10;
    this.intersectionObserver = null;
  }

  connectedCallback() {
    this._unbind();

    this.panels = Array.from(
      this.querySelectorAll("[data-smart-battery-panel]")
    );
    this.moCopies = Array.from(
      this.querySelectorAll("[data-smart-battery-mo]")
    );
    this.triggers = Array.from(
      this.querySelectorAll("[data-smart-battery-trigger]")
    );
    this.indicator = this.querySelector("[data-smart-battery-indicator]");
    this.tabRow = this.querySelector(".smart-battery-system__tab-row");
    this.tabRule = this.querySelector(".smart-battery-system__tab-rule");

    this.autoEnabled = this.dataset.enableAutoSwitch === 'true';
    this.autoInterval = Math.max(3, parseInt(this.dataset.autoSwitchInterval, 10) || 10) * 1000;

    this.triggers.forEach((btn) => {
      btn.addEventListener("click", this._onTriggerClick);
    });
    if (this.tabRow) {
      this.tabRow.addEventListener("scroll", this._onTabRowScroll);
    }
    window.addEventListener("resize", this._onResize);
    requestAnimationFrame(this._onResize);
    this.setActive(0);

    if (this.autoEnabled) {
      this._setupObserver();
    }
  }

  disconnectedCallback() {
    this._unbind();
  }

  _unbind() {
    this._stopAutoSwitch();
    this._teardownObserver();
    this.triggers.forEach((btn) => {
      btn.removeEventListener("click", this._onTriggerClick);
    });
    if (this.tabRow) {
      this.tabRow.removeEventListener("scroll", this._onTabRowScroll);
    }
    window.removeEventListener("resize", this._onResize);
  }

  _setupObserver() {
    if (this.intersectionObserver) return;
    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this._startAutoSwitch();
        } else {
          this._stopAutoSwitch();
        }
      }
    }, { threshold: 0.1 });
    this.intersectionObserver.observe(this);
  }

  _teardownObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  _startAutoSwitch() {
    if (this.switchTimer != null) return;
    if (this.triggers.length < 2) return;
    this.switchTimer = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.panels.length;
      this.setActive(this.currentIndex);
    }, this.autoInterval);
  }

  _stopAutoSwitch() {
    if (this.switchTimer != null) {
      clearInterval(this.switchTimer);
      this.switchTimer = null;
    }
  }

  /**
   * @param {Event} ev
   */
  _onTriggerClick(ev) {
    const btn = ev.currentTarget;
    if (!(btn instanceof Element)) return;
    const idx = parseInt(
      btn.getAttribute("data-smart-battery-trigger") || "",
      10
    );
    if (Number.isNaN(idx)) return;
    this.setActive(idx);
    if (this.autoEnabled) {
      this._stopAutoSwitch();
      this._startAutoSwitch();
    }
  }

  _onTabRowScroll() {
    const sel = this.querySelector(
      '.smart-battery-system__tab[aria-selected="true"]'
    );
    if (!sel) return;
    this.moveIndicator(
      parseInt(sel.getAttribute("data-smart-battery-trigger") || "-1", 10)
    );
  }

  _onResize() {
    const sel = this.querySelector(
      '.smart-battery-system__tab[aria-selected="true"]'
    );
    if (!sel) return;
    const idx = parseInt(
      sel.getAttribute("data-smart-battery-trigger") || "-1",
      10
    );
    this.moveIndicator(idx);
  }

  /**
   * @param {number} index
   */
  setActive(index) {
    this.currentIndex = index;
    this.panels.forEach((el, i) => {
      if (i === index) {
        if (el.dataset.heading) {
          const copyTitleDom = this.querySelector('.smart-battery-system__copy--pc .smart-battery-system__copy-title')
          if (copyTitleDom) copyTitleDom.innerText = el.dataset.heading
        }
        if (el.dataset.body) {
          const copyBodyDom = this.querySelector('.smart-battery-system__copy--pc .smart-battery-system__copy-body')
          if (copyBodyDom) copyBodyDom.innerHTML = el.dataset.body
        }
        el.removeAttribute("hidden");
      } else {
        el.setAttribute("hidden", "");
      }
    });
    this.moCopies.forEach((el, i) => {
      if (i === index) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });
    this.triggers.forEach((el) => {
      const i = parseInt(
        el.getAttribute("data-smart-battery-trigger") || "-1",
        10
      );
      const on = i === index;
      el.setAttribute("aria-selected", on ? "true" : "false");
      if (el.classList.contains("smart-battery-system__thumb")) {
        el.classList.toggle("is-active", on);
        const label = el.querySelector(".smart-battery-system__thumb-label");
        if (label) label.toggleAttribute("hidden", on);
      }
    });
    if (
      this.tabRow &&
      window.matchMedia("(max-width: 719px)").matches
    ) {
      const tabs = this.tabRow.querySelectorAll(".smart-battery-system__tab");
      const activeTab = tabs[index];
      if (activeTab) {
        const targetLeft = activeTab.offsetLeft - (this.tabRow.clientWidth / 2) + (activeTab.offsetWidth / 2);
        const maxLeft = this.tabRow.scrollWidth - this.tabRow.clientWidth;
        this.tabRow.scrollTo({ left: Math.max(0, Math.min(targetLeft, maxLeft)), behavior: "smooth" });
        const finalize = () => this.moveIndicator(index);
        this.tabRow.addEventListener("scrollend", finalize, { once: true });
        setTimeout(finalize, 360);
        return;
      }
    }
    this.moveIndicator(index);
  }

  /**
   * @param {number} index
   */
  moveIndicator(index) {
    if (!this.indicator || !this.tabRow || !this.tabRule) return;
    if (window.matchMedia("(min-width: 720px)").matches) {
      this.indicator.setAttribute("hidden", "");
      return;
    }
    const tabs = this.tabRow.querySelectorAll(".smart-battery-system__tab");
    const tab = tabs[index];
    if (!tab) return;
    this.indicator.removeAttribute("hidden");
    const rl = this.tabRule.getBoundingClientRect();
    const tl = tab.getBoundingClientRect();
    this.indicator.style.width = `${tl.width}px`;
    this.indicator.style.left = `${tl.left - rl.left}px`;
  }
}

if (!customElements.get("smart-battery-system")) {
  customElements.define("smart-battery-system", SmartBatterySystem);
}
